import React, { useEffect, useState } from 'react';
import { ScrollableContent } from 'src/components/ScrollableContent';
import { Link, useParams } from 'react-router-dom';
import SharedConfirmModal from 'src/components/ConfirmModal';
import { toast } from 'react-toastify';
import {
  fetchProjectByProjectId,
  fetchProjectUserByProjectId,
  revokeProgramManagerAccess,
} from 'src/redux/features/projects/projectsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from 'react-oidc-context';
import { TableLoader } from 'src/views/TimeSheet/components/TableLoader';
import { Card, Checkbox, Flex, Input, Spin, Table, Typography } from 'antd';
import AntDButton from 'src/components/Button';
import en from 'src/messages/en.json';
import { LeftOutlined } from '@ant-design/icons';
import { setError } from 'src/redux/features/common/commonSlice';

const EditProjectAccess = () => {
  const { Title } = Typography;
  const auth = useAuth();
  const dispatch = useDispatch();
  const { id: projectId } = useParams();
  const [value, setValue] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const { data: userLists, loading: userListsLoading } = useSelector(
    fetchProjectUserByProjectId,
  );

  const [checkedList, setCheckedList] = useState(
    data?.flatMap((project) =>
      project?.programManagers?.map((list) => list.programManagerId),
    ),
  );

  const handleCheckboxChange = (id) => {
    setCheckedList((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const checkAll =
    filteredUsers.length > 0 &&
    filteredUsers.every((user) => checkedList.includes(user.programManagerId));

  const handleSelectAllChange = (e) => {
    setCheckedList(
      e.target.checked
        ? filteredUsers.map((user) => user.programManagerId)
        : [],
    );
  };

  const confirmSubmit = () => {
    setShowConfirmModal(true);
  };

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      const resources = checkedList.map((id) => {
        const manager = filteredUsers.find(
          (user) => user.programManagerId === id,
        );

        return {
          programManagerId: id,
          resourceEmail: manager.resourceEmail,
        };
      });

      const payload = {
        projectId: parseInt(projectId, 10),
        resources,
        itProjectLeaderEmail: auth?.user?.profile?.email,
      };

      const response = await dispatch(
        revokeProgramManagerAccess(payload),
      ).unwrap();

      setData(response);

      toast.success(en.Access.Success.accessRevoked);
      setCheckedList([]);
    } catch (error) {
      dispatch(setError(en.Access.Error.revokeAccessFailed));
    }
    setIsSubmitting(false);
    setShowConfirmModal(false);
    setIsDataLoading(false);
  };

  const columns = [
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            checked={checkAll}
            disabled={!filteredUsers.length}
            onChange={handleSelectAllChange}
          />
          <span style={{ marginLeft: '1rem' }}>Users</span>
        </div>
      ),
      dataIndex: 'resourceName',
      key: 'resourceName',
      render: (name, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            checked={checkedList.includes(record.programManagerId)}
            onChange={() => handleCheckboxChange(record.programManagerId)}
          />
          <span style={{ marginLeft: '1rem' }}>{name}</span>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (value.trim() === '') {
      setFilteredUsers(data.flatMap((project) => project.programManagers));
    } else {
      const filtered = data
        .flatMap((project) => project.programManagers)
        .filter((user) =>
          user.resourceName.toLowerCase().includes(value.toLowerCase()),
        );

      setFilteredUsers(filtered);
    }
  }, [value, data]);

  useEffect(() => {
    const fetchProjects = async () => {
      setIsDataLoading(true);
      try {
        const response = await dispatch(
          fetchProjectByProjectId({
            ProjectId: projectId,
          }),
        ).unwrap();

        setData(response);
      } catch (error) {
        dispatch(setError(en.Common.Error.fetchData));
      }
      setIsDataLoading(false);
    };

    fetchProjects();
  }, [dispatch, projectId]);
  useEffect(() => {
    return () => {
      setCheckedList([]);
    };
  }, []);

  return (
    <>
      <Title level={4} style={{ textAlign: 'left' }}>
        {en.Access.Title.editAccess}
      </Title>
      <Flex gap={'middle'} vertical>
        <Card size="small">
          <Flex gap={'middle'} align="center">
            <Link to={'/project-access'}>
              <LeftOutlined style={{ color: 'black' }} />
            </Link>

            <Typography.Text ellipsis>
              {data.map((x) => x.projectName + ' ' + 'Access Overview')}
            </Typography.Text>
          </Flex>
        </Card>
        <Card size="small">
          <Flex gap={'middle'} vertical>
            <Flex justify="flex-start">
              <Input
                type="text"
                placeholder={en.Common.Placeholder.searchHere}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                style={{
                  width: '20rem',
                }}
                allowClear
              />
            </Flex>
            <ScrollableContent
              minHeight="calc(100vh - 18.75rem)"
              maxHeight="calc(100vh - 18.75rem)"
            >
              {isDataLoading ? (
                <Spin />
              ) : (
                <Table
                  columns={columns}
                  dataSource={filteredUsers}
                  rowKey="programManagerId"
                  pagination={false}
                  loading={isDataLoading}
                  rowClassName={(_, index) =>
                    index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
                  }
                  sticky
                />
              )}
            </ScrollableContent>
            <Flex gap={'middle'} justify="flex-end">
              <AntDButton
                type="default"
                style={{
                  backgroundColor: '#D9534F',
                  color: 'white',
                  borderColor: '#D9534F',
                }}
                onClick={confirmSubmit}
                disabled={!checkedList.length}
                htmlType="button"
                status=" Revoking..."
                statusOnComplete="Revoke Access"
                loading={isSubmitting}
              />
            </Flex>
            <SharedConfirmModal
              title={en.Common.Title.warning}
              description={
                'Are you sure you want to Revoke Project Access to these User(s) ?'
              }
              show={showConfirmModal}
              onCancel={() => setShowConfirmModal(false)}
              onConfirm={() => {
                setShowConfirmModal(false);
                onSubmit();
              }}
              style={{
                backgroundColor: '#D9534F',
                color: 'white',
                borderColor: '#D9534F',
              }}
            />
          </Flex>
        </Card>
      </Flex>
    </>
  );
};

export default EditProjectAccess;
